import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMExportOutput,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import {lazy, ReactNode, Suspense} from 'react';

const LazyGuidanceContent = lazy(() =>
  import('./GuidanceContent').then((module) => ({default: module.GuidanceContent}))
);

export type SerializedGuidanceNode = Spread<
  {
    headingId: string;
    guidance: string;
    className?: string;
  },
  SerializedLexicalNode
>;

export class GuidanceDecoratorNode extends DecoratorNode<ReactNode> {
  __headingId: string;
  __guidance: string;
  __className?: string;

  static getType(): string {
    return 'Guidance';
  }

  static clone(node: GuidanceDecoratorNode): GuidanceDecoratorNode {
    return new GuidanceDecoratorNode(
      node.__headingId,
      node.__guidance,
      node.__className,
      node.__key
    );
  }

  constructor(headingId: string, guidance: string, className?: string, key?: NodeKey) {
    super(key);
    this.__headingId = headingId;
    this.__guidance = guidance;
    this.__className = className;
  }

  createDOM(): HTMLElement {
    const element = document.createElement('div');
    element.setAttribute('data-lexical-guidance', 'true');
    return element;
  }

  updateDOM(): boolean {
    return false;
  }

  exportDOM(): DOMExportOutput {
    // Return null to exclude from copy/paste operations
    return {element: null};
  }

  static importDOM(): null {
    // Don't import guidance nodes from external content
    return null;
  }

  static importJSON(serializedNode: SerializedGuidanceNode): GuidanceDecoratorNode {
    const {headingId, guidance, className} = serializedNode;
    return $createGuidanceDecoratorNode(headingId, guidance, className);
  }

  exportJSON(): SerializedGuidanceNode {
    return {
      headingId: this.__headingId,
      guidance: this.__guidance,
      className: this.__className,
      type: 'Guidance',
      version: 1,
    };
  }

  getTextContent(): string {
    // Return empty string so it doesn't affect text content
    return '';
  }

  isKeyboardSelectable(): boolean {
    // Make this node non-navigable with keyboard
    return false;
  }

  getHeadingId(): string {
    return this.__headingId;
  }

  getGuidance(): string {
    return this.__guidance;
  }

  getClassName(): string | undefined {
    return this.__className;
  }

  setGuidance(guidance: string): void {
    const writable = this.getWritable();
    writable.__guidance = guidance;
  }

  setClassName(className: string | undefined): void {
    const writable = this.getWritable();
    writable.__className = className;
  }

  component(): ReactNode {
    return null;
  }

  decorate(): ReactNode {
    return (
      <Suspense fallback={null}>
        <LazyGuidanceContent guidance={this.__guidance} className={this.__className} />
      </Suspense>
    );
  }
}

export function $createGuidanceDecoratorNode(
  headingId: string,
  guidance: string,
  className?: string
): GuidanceDecoratorNode {
  return $applyNodeReplacement(new GuidanceDecoratorNode(headingId, guidance, className));
}

export function $isGuidanceDecoratorNode(node: unknown): node is GuidanceDecoratorNode {
  return node instanceof GuidanceDecoratorNode;
}
